// @ts-check
import { Component } from '@theme/component';

/**
 * @typedef {object} TextReadMoreRefs
 * @property {HTMLElement} content
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} moreText
 * @property {HTMLElement} lessText
 */

/**
 * Text Read More component
 * Displays only the first paragraph and hides the rest behind a toggle button.
 * Content remains in the DOM for SEO purposes.
 * @extends {Component<TextReadMoreRefs>}
 */
class TextReadMore extends Component {
  /** @type {boolean} */
  isExpanded = false;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setupContent();
  }

  #setupContent() {
    if (this.#initialized || !this.refs.content) return;

    // Handle metafield wrapper case (Shopify wraps metafield content in a div)
    /** @type {HTMLElement} */
    let contentContainer = this.refs.content;
    const metafieldWrapper = this.refs.content.querySelector('.metafield-rich_text_field');
    if (metafieldWrapper instanceof HTMLElement) {
      contentContainer = metafieldWrapper;
    }

    const children = Array.from(contentContainer.children);
    if (children.length <= 1) {
      // Only one element, hide the button
      if (this.refs.button) {
        this.refs.button.style.display = 'none';
      }
      return;
    }

    // Keep first element visible, wrap the rest in expandable container
    const firstElement = children[0];
    if (!firstElement) return;
    const remainingElements = children.slice(1);

    // Create expandable wrapper with inner container for grid animation
    const expandable = document.createElement('div');
    expandable.className = 'text-block__expandable';

    const inner = document.createElement('div');
    inner.className = 'text-block__expandable-inner';

    // Move remaining elements to inner container
    for (const el of remainingElements) {
      inner.appendChild(el);
    }

    expandable.appendChild(inner);

    // Insert after first element
    firstElement.after(expandable);

    this.#initialized = true;
  }

  toggleReadMore() {
    this.isExpanded = !this.isExpanded;
    this.classList.toggle('is-expanded', this.isExpanded);

    if (this.refs.button) {
      this.refs.button.setAttribute('aria-expanded', String(this.isExpanded));
    }
  }
}

if (!customElements.get('text-read-more')) {
  customElements.define('text-read-more', TextReadMore);
}
